import { useCallback, useEffect, useState } from 'react';
import type { DeviceDetail } from '@mantelpiece/shared';
import { api, type CarriedAlbum, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import type { Guard } from '../pages/Device';

interface Props {
  readonly session: StoredSession;
  readonly device: DeviceDetail;
  readonly guard: Guard;
}

/**
 * The albums this frame is carrying — the frame owner's side of a publication.
 *
 * Somebody else's album can be putting photos on your television, and this is
 * where you stop it. Without this tab the only way out would be to ask them,
 * which is not a sharing model anybody should have to live with.
 */
export const AlbumsTab = ({ session, device, guard }: Props) => {
  const [albums, setAlbums] = useState<readonly CarriedAlbum[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    await guard(async () => {
      setAlbums((await api.listCarriedAlbums(session.token, device.id)).albums);
    });
    setLoading(false);
  }, [device.id, guard, session.token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="empty">Loading</p>;

  if (albums.length === 0) {
    return (
      <p className="empty">
        No albums on this frame. An album keeps itself up to date — put one here from the Albums
        page and it stays in step on its own.
      </p>
    );
  }

  return (
    <section>
      <div className="section-head">
        <p className="eyebrow">
          Albums on this frame · {albums.length}
        </p>
      </div>
      <p className="note" style={{ marginBottom: '1.25rem' }}>
        These update themselves. When the person who keeps one adds a photo it appears here, and
        when they take one out it comes down.
      </p>

      <ul className="ledger">
        {albums.map((album) => (
          <li key={album.albumId}>
            <span className="ledger-main">
              <span className="ledger-name">{album.label}</span>
              <span className="ledger-meta">
                {album.ownerName === session.account.name ? 'yours' : `${album.ownerName}'s`} ·{' '}
                {album.photoCount} photo{album.photoCount === 1 ? '' : 's'} on this frame
              </span>
            </span>
            <span className="row-tight">
              {album.ownerName === session.account.name && (
                <button
                  type="button"
                  className="button button-quiet button-small"
                  onClick={() => go(`/a/${album.albumId}`)}
                >
                  Open
                </button>
              )}
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() =>
                  void guard(async () => {
                    await api.unpublishAlbum(session.token, album.albumId, device.id);
                    await load();
                  })
                }
              >
                Stop carrying it
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
